import React, { useMemo } from 'react'
import { Card, CardContent } from '@duck/ui'
import { useAssignments } from '@/hooks/useAssignments'
import { usePersons } from '@/hooks/usePersons'
import { CohortPersonDetails } from '../cohortPageData'
import { PartnerService } from '@duck/api-definitions'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { AssignmentEntry } from './AssignmentEntry'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { AssignmentsListSkeleton } from './AssignmentsListSkeleton'
import { useTar } from '@/modules'

interface AssignmentsListProps {
  taskAssignments: Record<string, CohortPersonDetails[]>
  assignmentsDetails?: PartnerService.AssignmentDetails[]
  onAssignmentChange: () => void
  skeleton?: boolean
}


export const AssignmentsList: React.FC<AssignmentsListProps> = ({
  taskAssignments,
  onAssignmentChange,
  assignmentsDetails = [],
  skeleton = false
}) => {
  const { t: tP } = usePartnerTar()
  const { t } = useTar()
  // Transform assignmentsDetails to group by assignmentId
  const assignmentsByAssignmentId = useMemo(() => {
    if (!assignmentsDetails || assignmentsDetails.length === 0) {
      return {}
    }

    // Flatten taskAssignments to create a map of personId -> personDetails
    const personDetailsMap = new Map<string, CohortPersonDetails>()
    Object.values(taskAssignments).forEach(personDetailsArray => {
      personDetailsArray.forEach(pd => {
        // Use personId as key, but we might have multiple entries per person (different tasks)
        // So we'll match by personId + taskId when possible
        personDetailsMap.set(pd.personId, pd)
      })
    })

    const result: Record<string, CohortPersonDetails[]> = {}

    assignmentsDetails.forEach(assignmentDetail => {
      const assignmentId = assignmentDetail.assignmentId
      if (!result[assignmentId]) {
        result[assignmentId] = []
      }

      // Find the person details from the map
      const basePersonDetails = personDetailsMap.get(assignmentDetail.personId)

      if (basePersonDetails) {
        // Use the base person details but update with assignment-specific info
        result[assignmentId].push({
          ...basePersonDetails,
          assignmentStatus: assignmentDetail.status,
          grade: assignmentDetail.grade,
          feedback: assignmentDetail.feedback,
          solution: assignmentDetail.solution,
          startDate: assignmentDetail.startDate,
          submitDate: assignmentDetail.submitDate,
          workTime: assignmentDetail.workTime,
          charts: assignmentDetail.charts,
        })
      }
    })

    return result
  }, [taskAssignments, assignmentsDetails])

  const assignmentIds = useMemo(() => Object.keys(assignmentsByAssignmentId), [assignmentsByAssignmentId])
  const { assignments: assignmentsData, isLoading: isLoadingAssignments, refetch: refetchAssignments } = useAssignments(assignmentIds)
  const assignments: Record<string, PartnerService.Assignment> = assignmentsData

  // Get all personIds from all assignments
  const allPersonIds = useMemo(() => {
    return Array.from(new Set(Object.values(assignmentsByAssignmentId).flat().map(pd => pd.personId)))
  }, [assignmentsByAssignmentId])

  const { persons, isLoading: isLoadingPersons } = usePersons(allPersonIds)

  // Get taskIds for display names
  const taskIds = useMemo(() => {
    return Array.from(new Set(Object.values(assignments).map(a => a.taskId)))
  }, [assignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })


  if (skeleton) {
    return <AssignmentsListSkeleton variant="detailed" />
  }
  const isLoading = isLoadingAssignments || isLoadingPersons

  if (isLoading) {
    return <AssignmentsListSkeleton variant="simple" />
  }

  if (assignmentIds.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              icon={ClipboardDocumentListIcon}
              title={tP('noAssignmentsYet')}
              description={tP('noAssignmentsDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {assignmentIds.map(assignmentId => {
        const assignment = assignments[assignmentId]
        const peopleDetails = assignmentsByAssignmentId[assignmentId] || []
        const personIds = peopleDetails.map(pd => pd.personId)
        const assignmentPersons = persons.filter(p => personIds.includes(p.id))

        if (!assignment) {
          return null
        }

        return (
          <AssignmentEntry
            key={assignmentId}
            assignment={assignment}
            peopleDetails={peopleDetails}
            assignmentPersons={assignmentPersons}
            getDisplayName={getDisplayName}
            isTaskLoading={isTaskLoading}
            onAssignmentChange={() => {
              refetchAssignments()
              onAssignmentChange()
            }}
          />
        )
      })}
    </div>
  )
}
